import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  InputAdornment,
  Chip,
  Autocomplete,
  FormControlLabel,
  Switch
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import { FaArrowLeft, FaSearch, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { meetingsConfigurationApi } from '../../../services/api/meetingsConfigurationApi';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, ItemsGrid, ItemCard, ItemCardContent, ItemTitle, ItemDetailsGrid, OrderBadge, ActionButtons, ItalicText, Label } from '../styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';

const TalkingPoints = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [items, setItems] = useState([]);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    question: '',
    instructions: '',
    order: 0,
    isSystemTalkingPoint: true,
    publishedAt: null,
    meetingTypeIds: []
  });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMeetingTypes, setSelectedMeetingTypes] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [talkingPointsData, meetingTypesData] = await Promise.all([
        meetingsConfigurationApi.getAllTalkingPoints(),
        meetingsConfigurationApi.getAllMeetingTypes()
      ]);
      setItems(talkingPointsData);
      setMeetingTypes(meetingTypesData);
    } catch (err) {
      showAlert('Failed to load talking points.', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setCurrentItem({
        ...item,
        meetingTypeIds: item.meetingTypes?.map(mt => mt.meetingType?.id || mt.meetingTypeId) || []
      });
      setSelectedMeetingTypes(
        item.meetingTypes?.map(mt => meetingTypes.find(t => t.id === (mt.meetingType?.id || mt.meetingTypeId))).filter(Boolean) || []
      );
      setIsEditing(true);
    } else {
      setCurrentItem({
        question: '',
        instructions: '',
        order: items.length + 1,
        isSystemTalkingPoint: true,
        publishedAt: null,
        meetingTypeIds: []
      });
      setSelectedMeetingTypes([]);
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (item) => {
    setCurrentItem(item);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentItem({
      ...currentItem,
      [name]: type === 'checkbox' ? checked : (name === 'order' ? parseInt(value, 10) || 0 : value)
    });
  };

  const handleMeetingTypesChange = (event, newValue) => {
    setSelectedMeetingTypes(newValue);
    setCurrentItem({
      ...currentItem,
      meetingTypeIds: newValue.map(t => t.id)
    });
  };

  const handlePublishToggle = () => {
    setCurrentItem({
      ...currentItem,
      publishedAt: currentItem.publishedAt ? null : new Date().toISOString()
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const payload = {
        question: currentItem.question,
        instructions: currentItem.instructions,
        order: currentItem.order,
        isSystemTalkingPoint: currentItem.isSystemTalkingPoint,
        publishedAt: currentItem.publishedAt,
        meetingTypeIds: currentItem.meetingTypeIds
      };

      if (isEditing) {
        await meetingsConfigurationApi.updateTalkingPoint(currentItem.id, payload);
        showAlert('Talking point updated successfully', 'success');
      } else {
        await meetingsConfigurationApi.createTalkingPoint(payload);
        showAlert('Talking point created successfully', 'success');
      }
      handleCloseDialog();
      await fetchData();
    } catch (err) {
      showAlert(err.message || `Failed to ${isEditing ? 'update' : 'create'} talking point.`, 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await meetingsConfigurationApi.deleteTalkingPoint(currentItem.id);
      showAlert('Talking point deleted successfully', 'success');
      handleCloseDeleteDialog();
      await fetchData();
    } catch (err) {
      showAlert(err.message || 'Failed to delete talking point.', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item =>
    item.question?.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
    item.instructions?.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  if (loading && items.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/meetings-configuration')}>
            <FaArrowLeft /> Back to Meetings Configuration
          </BackButton>
          <Title>Talking Points Configuration</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={true} style={{ marginBottom: '20px' }}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Talking Points"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                  }
                }}
              />
            </Box>

            <HeaderButton
              onClick={() => handleOpenDialog()}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Add Talking Point
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer>
          <div style={{
            color: '#64748B',
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ fontWeight: 600 }}>
              {filteredItems.length}
            </span>
            {filteredItems.length === 1 ? 'talking point' : 'talking points'} found
          </div>

          {filteredItems.length === 0 ? (
            <EmptyState
              title="No talking points found"
              description={searchTerm ?
                "No talking points match your search criteria." :
                "No talking points have been added yet. Click the '+ Add Talking Point' button to add your first talking point."}
            />
          ) : (
            <ItemsGrid>
              {filteredItems.map((item) => (
                <ItemCard key={item.id}>
                  <ItemCardContent>
                    <ItemTitle>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        {item.question}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {item.publishedAt ? (
                          <Chip label="Published" size="small" color="success" />
                        ) : (
                          <Chip label="Draft" size="small" color="warning" />
                        )}
                        <OrderBadge>
                          Order: {item.order}
                        </OrderBadge>
                      </Box>
                    </ItemTitle>
                    {item.instructions && (
                      <Box sx={{ mb: 1 }}>
                        <Label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Instructions:</Label>
                        <ItemDetailsGrid style={{ marginBottom: 0 }}>
                          {item.instructions}
                        </ItemDetailsGrid>
                      </Box>
                    )}
                    {item.meetingTypes && item.meetingTypes.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Meeting Types:</Label>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {item.meetingTypes.map((mt, idx) => (
                            <Chip
                              key={idx}
                              label={mt.meetingType?.name || 'Unknown'}
                              size="small"
                              sx={{
                                backgroundColor: `${mt.meetingType?.color || '#666'}20`,
                                color: mt.meetingType?.color || '#666',
                                fontSize: '11px'
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {(!item.meetingTypes || item.meetingTypes.length === 0) && (
                      <Box sx={{ mb: 1 }}>
                        <ItalicText style={{ fontSize: '12px' }}>Available for all meeting types</ItalicText>
                      </Box>
                    )}
                    <ActionButtons>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(item)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ActionButtons>
                  </ItemCardContent>
                </ItemCard>
              ))}
            </ItemsGrid>
          )}
        </MainContainer>
      </Content>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Talking Point' : 'Add Talking Point'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="question"
            label="Question / Topic"
            type="text"
            fullWidth
            variant="outlined"
            value={currentItem.question}
            onChange={handleInputChange}
            multiline
            rows={2}
          />
          <TextField
            margin="dense"
            name="instructions"
            label="Instructions (Optional)"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={currentItem.instructions}
            onChange={handleInputChange}
            helperText="Additional guidance for facilitators on how to discuss this topic"
          />
          <Autocomplete
            multiple
            options={meetingTypes}
            getOptionLabel={(option) => option.name}
            value={selectedMeetingTypes}
            onChange={handleMeetingTypesChange}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Meeting Types (Optional)"
                placeholder="Select meeting types"
                helperText="Leave empty to make available for all meeting types"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: option.color || '#6b7280',
                      flexShrink: 0,
                    }}
                  />
                  {option.name}
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  size="small"
                  {...getTagProps({ index })}
                  sx={{
                    backgroundColor: `${option.color}20`,
                    color: option.color
                  }}
                />
              ))
            }
            sx={{ mt: 1 }}
          />
          <TextField
            margin="dense"
            name="order"
            label="Display Order"
            type="number"
            fullWidth
            variant="outlined"
            value={currentItem.order}
            onChange={handleInputChange}
          />
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!currentItem.publishedAt}
                  onChange={handlePublishToggle}
                  color="success"
                />
              }
              label={currentItem.publishedAt ? 'Published' : 'Draft'}
            />
            {currentItem.publishedAt && (
              <Typography variant="caption" color="text.secondary">
                Will be visible to users when creating meetings
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!currentItem.question?.trim()}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this talking point? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TalkingPoints;
